import type { NavKey } from '@/shared/layout/nav/types';
import { menuItemsByRail } from '@/shared/layout/nav/menuByRail';
import type { SpotlightActionDef, SpotlightIconId } from './actionCatalog';
import { normalizeQuery } from './normalizeQuery';

const NAV_ICON_BY_LABEL: Partial<Record<NavKey, SpotlightIconId>> = {
  dashboard: 'dashboard',
  patients: 'patient',
  leads: 'lead',
  appointments: 'appointment',
  tasks: 'task',
  projects: 'project',
  kpiSheets: 'report',
  kpiMetrics: 'report',
  myKpi: 'report',
  payments: 'payment',
  expenses: 'expense',
  incomes: 'payment',
  staff: 'staff',
  departments: 'staff',
  workTimesheet: 'report',
  reportRevenue: 'report',
  reportPatients: 'report',
  reportReferrers: 'report',
  referrers: 'patient',
  reportAppointments: 'report',
  reportTreatments: 'report',
  leadReports: 'report',
  inventoryReports: 'report',
  treatmentPlans: 'flash',
};

const EXTRA_KEYWORDS: Partial<Record<NavKey, string[]>> = {
  projects: ['du an', 'project'],
  kpiSheets: ['kpi', 'chi tieu', 'hieu suat', 'kpi cua toi'],
  myKpi: ['kpi cua toi', 'my kpi'],
  stockIn: ['kho', 'nhap kho', 'inventory'],
  stockOut: ['kho', 'xuat kho'],
  transfers: ['kho', 'dieu chuyen'],
  stocktakes: ['kho', 'kiem ke'],
  inventoryReports: ['kho', 'bao cao kho', 'ton kho'],
  itemCatalog: ['kho', 'danh muc hang'],
  warehouseConfig: ['kho', 'cau hinh kho', 'danh muc hang', 'nha cung cap', 'suppliers'],
  suppliers: ['nha cung cap', 'supplier'],
  departments: ['phong ban', 'department'],
  commissions: ['hoa hong', 'commission'],
  payroll: ['bang luong', 'luong', 'payroll', 'mau luong', 'cau truc luong', 'khoan luong', 'phieu luong'],
  cashbook: ['so quy', 'cashbook'],
  promotions: ['khuyen mai'],
  treatmentPlans: ['ke hoach', 'dieu tri'],
  treatmentSessions: ['nhat ky dieu tri', 'buoi dieu tri'],
  labOrders: ['lab', 'labo'],
  reportReferrers: ['bao cao nguoi gioi thieu', 'gioi thieu', 'referrer'],
  reportRevenue: ['bao cao doanh thu', 'doanh thu', 'revenue'],
  referrers: ['nguoi gioi thieu', 'gioi thieu', 'referrer'],
  labo: ['lab', 'labo', 'xuong', 'doi tac', 'vat lieu', 'mau', 'shade', 'loai phuc hinh'],
  attendanceHistory: ['cham cong', 'yeu cau', 'dieu chinh'],
  workTimesheet: [
    'bao cao nhat ky dieu tri',
    'nhat ky dieu tri',
    'bang viec',
    'so viec',
    'timesheet viec',
  ],
  teamSchedule: ['lich team', 'lich lam viec', 'loai ca', 'mau tuan', 'gan lich'],
  timeOff: ['nghi phep', 'don nghi', 'loai phep', 'han muc phep', 'so phep', 'bao cao phep'],
  automation: ['automation', 'tu dong hoa'],
  outbound: ['zns', 'sms', 'gui tin'],
  auditLog: ['nhat ky', 'audit'],
  permissionGroups: ['vai tro', 'role'],
};

/**
 * Flatten org sidebar menu into Spotlight navigate actions.
 * All entries are `hideWhenEmpty` so empty ⌘K stays focused on create/commands.
 */
export const buildNavSpotlightActions = (): SpotlightActionDef[] => {
  const actions: SpotlightActionDef[] = [];
  const seenPaths = new Set<string>();

  for (const items of Object.values(menuItemsByRail)) {
    for (const item of items) {
      if (seenPaths.has(item.path)) continue;
      seenPaths.add(item.path);

      actions.push({
        id: `nav-${item.labelKey}`,
        kind: 'navigate',
        group: 'go',
        navLabelKey: item.labelKey,
        // Spaced camelCase only — raw `auditLog` → `auditlog` false-matches
        // substrings like `tl` (audi**tl**og, leaveenti**tl**ements).
        keywords: [
          normalizeQuery(item.labelKey.replace(/([A-Z])/g, ' $1')),
          ...(EXTRA_KEYWORDS[item.labelKey] ?? []),
        ],
        path: item.path,
        icon: NAV_ICON_BY_LABEL[item.labelKey] ?? 'flash',
        permissionsAny: item.permissions,
        hideWhenEmpty: true,
      });
    }
  }

  actions.push({
    id: 'nav-clinics',
    kind: 'navigate',
    group: 'go',
    navLabelKey: 'clinics',
    keywords: [normalizeQuery('clinics'), 'phong kham', 'chi nhanh'],
    path: '/clinics',
    icon: 'flash',
    permissionsAny: ['clinics:read'],
    hideWhenEmpty: true,
  });

  actions.push({
    id: 'nav-payrollFormulas',
    kind: 'navigate',
    group: 'go',
    navLabelKey: 'payrollFormulas',
    keywords: [
      normalizeQuery('payroll Formulas'),
      'cong thuc luong',
      'muc luong',
      'mau luong',
    ],
    path: '/payroll?tab=formulas',
    icon: 'flash',
    permissionsAny: ['payroll:read'],
    hideWhenEmpty: true,
  });

  actions.push({
    id: 'nav-payrollComponents',
    kind: 'navigate',
    group: 'go',
    navLabelKey: 'payrollComponents',
    keywords: [normalizeQuery('payroll Components'), 'khoan luong'],
    path: '/payroll?tab=components',
    icon: 'flash',
    permissionsAny: ['payroll:read'],
    hideWhenEmpty: true,
  });

  actions.push({
    id: 'nav-payrollMine',
    kind: 'navigate',
    group: 'go',
    navLabelKey: 'payrollMine',
    keywords: [normalizeQuery('payroll Mine'), 'phieu luong', 'phieu cua toi'],
    path: '/profile?tab=payslips',
    icon: 'flash',
    permissionsAny: ['payroll:payslip-own'],
    hideWhenEmpty: true,
  });

  actions.push({
    id: 'nav-payrollAdvances',
    kind: 'navigate',
    group: 'go',
    navLabelKey: 'payrollAdvances',
    keywords: [normalizeQuery('payroll Advances'), 'tam ung', 'tam ung luong'],
    path: '/payroll?tab=advances',
    icon: 'flash',
    permissionsAny: ['payroll:read'],
    hideWhenEmpty: true,
  });

  actions.push({
    id: 'nav-commissionRules',
    kind: 'navigate',
    group: 'go',
    navLabelKey: 'commissionRules',
    keywords: [normalizeQuery('commission Rules'), 'hoa hong', 'quy tac'],
    path: '/commissions?tab=rules',
    icon: 'flash',
    permissionsAny: ['commission:read'],
    hideWhenEmpty: true,
  });

  actions.push({
    id: 'nav-timeOffTypes',
    kind: 'navigate',
    group: 'go',
    navLabelKey: 'timeOffTypes',
    keywords: [normalizeQuery('time Off Types'), 'loai phep'],
    path: '/scheduling/time-off?tab=types',
    icon: 'flash',
    permissionsAny: ['leave:read', 'leave:types:manage'],
    hideWhenEmpty: true,
  });

  actions.push({
    id: 'nav-leaveEntitlements',
    kind: 'navigate',
    group: 'go',
    navLabelKey: 'leaveEntitlements',
    keywords: [normalizeQuery('leave Entitlements'), 'han muc phep'],
    path: '/scheduling/time-off?tab=entitlements',
    icon: 'flash',
    permissionsAny: ['leave:entitlements:manage'],
    hideWhenEmpty: true,
  });

  actions.push({
    id: 'nav-leaveLedger',
    kind: 'navigate',
    group: 'go',
    navLabelKey: 'leaveLedger',
    keywords: [normalizeQuery('leave Ledger'), 'so phep'],
    path: '/scheduling/time-off?tab=ledger',
    icon: 'flash',
    permissionsAny: ['leave:entitlements:manage'],
    hideWhenEmpty: true,
  });

  actions.push({
    id: 'nav-leaveReport',
    kind: 'navigate',
    group: 'go',
    navLabelKey: 'leaveReport',
    keywords: [normalizeQuery('leave Report'), 'bao cao phep'],
    path: '/scheduling/time-off?tab=report',
    icon: 'report',
    permissionsAny: ['leave:entitlements:manage'],
    hideWhenEmpty: true,
  });

  actions.push({
    id: 'nav-shiftTypes',
    kind: 'navigate',
    group: 'go',
    navLabelKey: 'shiftTypes',
    keywords: [normalizeQuery('shift Types'), 'loai ca'],
    path: '/scheduling?tab=shiftTypes',
    icon: 'flash',
    permissionsAny: ['scheduling:read', 'scheduling:shift-types:manage'],
    hideWhenEmpty: true,
  });

  actions.push({
    id: 'nav-weeklyTemplates',
    kind: 'navigate',
    group: 'go',
    navLabelKey: 'weeklyTemplates',
    keywords: [normalizeQuery('weekly Templates'), 'mau lich', 'mau tuan'],
    path: '/scheduling?tab=templates',
    icon: 'flash',
    permissionsAny: ['scheduling:read', 'scheduling:templates:manage'],
    hideWhenEmpty: true,
  });

  actions.push({
    id: 'nav-assignments',
    kind: 'navigate',
    group: 'go',
    navLabelKey: 'assignments',
    keywords: [normalizeQuery('assignments'), 'gan lich'],
    path: '/scheduling?tab=templates',
    icon: 'flash',
    permissionsAny: ['scheduling:read', 'scheduling:assignments:manage'],
    hideWhenEmpty: true,
  });

  actions.push({
    id: 'nav-attendanceRequests',
    kind: 'navigate',
    group: 'go',
    navLabelKey: 'attendanceRequests',
    keywords: [normalizeQuery('attendance Requests'), 'yeu cau cham cong', 'dieu chinh'],
    path: '/attendance?tab=requests',
    icon: 'flash',
    permissionsAny: [
      'attendance:request',
      'attendance:approve',
      'attendance:manage',
    ],
    hideWhenEmpty: true,
  });

  return actions;
};

/** Prefer static curated actions when the same path appears in nav index. */
export const mergeSpotlightActions = (
  staticActions: SpotlightActionDef[],
  navActions: SpotlightActionDef[],
): SpotlightActionDef[] => {
  const seen = new Set(staticActions.map((a) => a.path));
  const uniqueNav = navActions.filter((a) => !seen.has(a.path));
  return [...staticActions, ...uniqueNav];
};
